import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@/providers/RouterShim';
import type { ApiResponse } from '../../@types/ApiResponse';
import type { Branch } from '../../@types/Branch';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import { getJSON } from '../../fetch';
import useAuth from '../../hooks/useAuth';
import { getHtmlLangWithLocaleOverride, getLanguageWithLocaleOverride } from '../../utils/market';

export const BRANCH_QUERY = '/api/organisation/GetBranch/:branchId';

const EN_US_BRANCH_IDS = [
  '34ef7105-b007-437b-99b2-6f3b07c3a3df',
  'e627c6f2-b787-47e0-90af-2bfc5f7fd557',
  'd8132a65-1c1f-4444-9cc5-77efc8caf228',
  'd60bee56-3648-40ae-9e95-f7574772aec9',
  'a692d7f7-7cea-4375-a8e9-75da9f8120fc',
  '5d76cbf5-a8cb-4772-8966-9f13aba03b10',
];

const useBranch = (): [Branch | undefined, RequestStateQuery] => {
  const { decodedToken, getToken } = useAuth();
  const { branchId } = useParams();
  const { i18n } = useTranslation();

  const branchExistInToken =
    branchId && decodedToken?.['belongs-to-branch']
      ? typeof decodedToken['belongs-to-branch'] === 'string'
        ? decodedToken['belongs-to-branch'] === branchId
        : decodedToken['belongs-to-branch'].includes(branchId)
      : false;

  const { data, isLoading, isSuccess, isError } = useQuery<ApiResponse<Branch>>({
    queryKey: [BRANCH_QUERY, branchId],
    queryFn: async () =>
      await getJSON<ApiResponse<Branch>>(`/api/organisation/GetBranch/${branchId}`, getToken()),
    enabled: !!branchId && !!branchExistInToken,
  });

  const marketCode = data?.response.marketCode;
  useEffect(() => {
    if (branchId && EN_US_BRANCH_IDS.includes(branchId)) {
      document.documentElement.setAttribute('lang', 'en-US');
      i18n.changeLanguage('en-US');
    } else if (marketCode) {
      // Check for locale cookie override before setting language
      const locale = getLanguageWithLocaleOverride(marketCode);
      const htmlLang = getHtmlLangWithLocaleOverride(marketCode);

      document.documentElement.setAttribute('data-market', marketCode);
      document.documentElement.setAttribute('lang', htmlLang);
      i18n.changeLanguage(locale);
    }
  }, [branchId, marketCode]);

  return [data?.response, { isLoading, isSuccess, isError }];
};

export default useBranch;
